import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../api";
import { AdminVisits } from "./AdminVisits";
import { QRCodeSVG } from "qrcode.react";

const CREAM_TABS = ["Tables", "Orders", "Menu", "Guests & Rewards", "QR Codes", "Settings"];
const STATUS_LABEL = {
  waiting_for_waiter: "Waiting for VeerGi", waiter_reviewing: "Being reviewed", confirmed: "Confirmed",
  pos_failed: "Retrying POS", sent_to_pos: "In kitchen", preparing: "Cooking", ready: "Ready",
  served: "Served", payment_requested: "Bill requested", paid: "Paid", completed: "Completed", cancelled: "Cancelled",
};

const Panel = ({ children }) => (
  <div style={{ background: "#F3EBDD", borderRadius: 18, padding: 16 }}>{children}</div>
);
const Card = ({ children, testId }) => (
  <div data-testid={testId} style={{ background: "#FFFDF8", borderRadius: 14, padding: 14, boxShadow: "0 1px 6px rgba(62,42,26,0.06)" }}>{children}</div>
);

const TablesTab = ({ data, reload }) => {
  const change = async (o) => {
    const t = window.prompt(`Move order ${o.order_id} from table ${o.table} to which table?`, o.table);
    if (!t || t === o.table) return;
    try {
      const r = await api.post("/admin/tables/change", { order_id: o.order_id, table: t.trim() });
      toast.success(`Moved to table ${r.data.table} — POS: ${r.data.pos}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change table"); }
  };
  const open = data?.open || [];
  return (
    <Panel>
      <div data-testid="cream-tables-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 10 }}>
        {open.map((o) => (
          <Card key={o.order_id} testId={`cream-table-card-${o.order_id}`}>
            <p style={{ fontSize: 20, fontWeight: 800, color: "#3E2A1A", margin: 0 }}>Table {o.table}</p>
            <p style={{ fontSize: 11, fontWeight: 700, color: "#B8892B", margin: "2px 0 6px" }}>{STATUS_LABEL[o.status] || o.status}{o.paid ? " · PAID" : ""}</p>
            <p style={{ fontSize: 12, color: "#9a8f7c", margin: 0 }}>{o.items} item{o.items === 1 ? "" : "s"} · ${o.subtotal?.toFixed(2)}{o.customer ? ` · ${o.customer}` : ""}</p>
            <button data-testid={`cream-change-table-${o.order_id}`} onClick={() => change(o)}
              style={{ marginTop: 10, width: "100%", background: "#fff", border: "1.5px solid #B8892B", color: "#8a651c", borderRadius: 999, padding: "8px 0", fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", cursor: "pointer" }}>
              CHANGE TABLE
            </button>
          </Card>
        ))}
        {!open.length && <p style={{ fontSize: 13, color: "#9a8f7c" }}>No open tables right now.</p>}
      </div>
    </Panel>
  );
};

const OrdersTab = ({ data }) => (
  <Panel>
    <div data-testid="cream-orders-list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {[...(data?.open || []), ...(data?.recent || [])].slice(0, 40).map((o) => (
        <Card key={o.order_id} testId={`cream-order-${o.order_id}`}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 800, color: "#3E2A1A", margin: 0 }}>{o.order_id} · Table {o.table}</p>
              <p style={{ fontSize: 11, color: "#9a8f7c", margin: "2px 0 0" }}>{STATUS_LABEL[o.status] || o.status} · {o.items} items · ${o.subtotal?.toFixed(2)}</p>
            </div>
            {o.paid && <span style={{ fontSize: 10, fontWeight: 800, background: "#6BA368", color: "#fff", borderRadius: 999, padding: "4px 10px" }}>PAID</span>}
          </div>
        </Card>
      ))}
      {!(data?.open?.length || data?.recent?.length) && <p style={{ fontSize: 13, color: "#9a8f7c" }}>No orders yet.</p>}
    </div>
  </Panel>
);

const MenuTab = () => {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/table-menu").then((r) => setM(r.data)).catch(() => {}); }, []);
  return (
    <Panel>
      <p style={{ fontSize: 12, color: "#9a8f7c", margin: "0 0 10px" }}>Read-only · Synced from the POS (veergigalla.com) — {m?.count ?? "…"} dishes. Names and prices change only when the POS changes.</p>
      <div data-testid="cream-menu-list" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {(m?.categories || []).map((c) => (
          <Card key={c}>
            <p style={{ fontSize: 13, fontWeight: 800, color: "#B8892B", margin: "0 0 6px", letterSpacing: "0.08em" }}>{c.toUpperCase()}</p>
            {(m?.dishes || []).filter((d) => d.category === c).map((d) => (
              <div key={d.slug} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "2px 0" }}>
                <span style={{ color: "#3E2A1A", fontWeight: 600 }}>{d.name}</span>
                <span style={{ color: "#9a8f7c", fontWeight: 700 }}>${Number(d.price).toFixed(2)}</span>
              </div>
            ))}
          </Card>
        ))}
      </div>
    </Panel>
  );
};

const QrTab = () => {
  const [count, setCount] = useState(12);
  const origin = window.location.origin;
  useEffect(() => { api.get("/admin/tables-config").then((r) => setCount(r.data.count)).catch(() => {}); }, []);
  const saveCount = async () => {
    try {
      const r = await api.put("/admin/tables-config", { count });
      setCount(r.data.count);
      toast.success(`Saved — guests can order from tables 1 to ${r.data.count}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  return (
    <Panel>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#3E2A1A" }}>Tables:</span>
        <input data-testid="cream-qr-count" type="number" min="1" max="60" value={count} onChange={(e) => setCount(Math.min(60, +e.target.value || 1))}
          style={{ width: 70, background: "#FFFDF8", border: "1px solid #E5D9BE", borderRadius: 10, padding: "8px 10px", fontWeight: 700, color: "#3E2A1A" }} />
        <button data-testid="cream-qr-save" onClick={saveCount}
          style={{ background: "#6BA368", color: "#fff", border: "none", borderRadius: 999, padding: "9px 18px", fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", cursor: "pointer" }}>
          SAVE
        </button>
        <button data-testid="cream-qr-print" onClick={() => window.print()}
          style={{ background: "#B8892B", color: "#fff", border: "none", borderRadius: 999, padding: "9px 18px", fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", cursor: "pointer" }}>
          PRINT
        </button>
      </div>
      <div data-testid="cream-qr-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
        {Array.from({ length: count }, (_, i) => i + 1).map((n) => (
          <Card key={n} testId={`cream-qr-${n}`}>
            <div style={{ textAlign: "center" }}>
              <QRCodeSVG value={`${origin}/t/${n}`} size={110} fgColor="#3E2A1A" bgColor="#FFFDF8" />
              <p style={{ fontSize: 13, fontWeight: 800, color: "#3E2A1A", margin: "8px 0 0" }}>TABLE {n}</p>
              <p style={{ fontSize: 9, color: "#9a8f7c", margin: 0 }}>{origin.replace(/^https?:\/\//, "")}/t/{n}</p>
            </div>
          </Card>
        ))}
      </div>
    </Panel>
  );
};

const SettingsTab = () => {
  const [cfg, setCfg] = useState(null);
  const [rate, setRate] = useState("");
  useEffect(() => {
    api.get("/admin/payments-settings").then((r) => { setCfg(r.data); setRate(r.data.tax_rate ?? 11.75); }).catch(() => setRate(11.75));
  }, []);
  const save = async () => {
    const v = rate === "" || rate === null || Number(rate) <= 0 ? 11.75 : Number(rate);
    try {
      const r = await api.put("/admin/payments-settings", { tax_rate: v });
      setCfg((prev) => ({ ...(prev || {}), ...r.data }));
      setRate(r.data.tax_rate ?? v);
      toast.success("Saved — guest bills use it right away");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  return (
    <Panel>
      <Card testId="cream-settings-card">
        <p style={{ fontSize: 14, fontWeight: 800, color: "#3E2A1A", margin: 0 }}>Tax %</p>
        <p style={{ fontSize: 12, color: "#9a8f7c", margin: "4px 0 10px" }}>
          Percent added to guest bills before tip. Default 11.75% — leaving it empty saves the default.
        </p>
        <div style={{ display: "flex", gap: 10 }}>
          <input data-testid="cream-tax-input" type="number" step="0.01" min="0" max="30" value={rate} onChange={(e) => setRate(e.target.value)}
            placeholder="e.g. 11.75"
            style={{ width: 120, background: "#F3EBDD", border: "1px solid #E5D9BE", borderRadius: 10, padding: "9px 12px", fontWeight: 700, color: "#3E2A1A" }} />
          <button data-testid="cream-tax-save" onClick={save}
            style={{ background: "#B8892B", color: "#fff", border: "none", borderRadius: 999, padding: "9px 20px", fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", cursor: "pointer" }}>
            SAVE
          </button>
        </div>
      </Card>
      <div style={{ height: 10 }} />
      <Card testId="cream-stripe-mode-card">
        <p style={{ fontSize: 14, fontWeight: 800, color: "#3E2A1A", margin: 0 }}>Stripe mode</p>
        <p data-testid="cream-stripe-mode" style={{ fontSize: 12, color: cfg?.stripe_mode === "live" ? "#6BA368" : "#B8892B", fontWeight: 800, margin: "4px 0 0", letterSpacing: "0.1em" }}>
          {cfg?.stripe_mode ? cfg.stripe_mode.toUpperCase() : "…"}
        </p>
        <p style={{ fontSize: 11, color: "#9a8f7c", margin: "4px 0 0" }}>Preview uses TEST. Production reads the Live key from Secrets (STRIPE_API_KEY).</p>
      </Card>
      <div style={{ height: 10 }} />
      <Card>
        <p style={{ fontSize: 12, color: "#9a8f7c", margin: 0 }}>Reward settings live under <b>Guests & Rewards</b>. Everything else is under <b>Advanced tools</b> — nothing was removed.</p>
      </Card>
    </Panel>
  );
};

export default function AdminCream({ onAdvanced, role = "owner" }) {
  const [tab, setTab] = useState("Tables");
  const [overview, setOverview] = useState(null);
  const tabs = role === "manager" ? CREAM_TABS.filter((t) => t !== "Settings") : CREAM_TABS;
  const load = () => api.get("/admin/dinein-overview").then((r) => setOverview(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  return (
    <div data-testid="cream-admin" style={{ minHeight: "100vh", background: "#FBF6EC", color: "#3E2A1A",
      fontFamily: '"Poppins", -apple-system, Helvetica, Arial, sans-serif', padding: "26px 18px 60px" }}>
      <style>{'@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700;800&family=Yeseva+One&display=swap");'}</style>
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
          <h1 style={{ fontFamily: '"Yeseva One", serif', fontSize: 34, color: "#C62828", fontWeight: 400, margin: 0 }}>Urban Turban Admin</h1>
          {onAdvanced && (
            <button data-testid="cream-advanced-link" onClick={onAdvanced}
              style={{ background: "none", border: "none", color: "#9a8f7c", fontSize: 12, fontWeight: 700, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>
              Advanced tools →
            </button>
          )}
        </div>
        <div data-testid="cream-tab-row" style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "18px 0" }}>
          {CREAM_TABS.filter((t) => tabs.includes(t)).map((t) => (
            <button key={t} data-testid={`cream-tab-${t.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`} onClick={() => setTab(t)}
              style={{ border: "none", cursor: "pointer", borderRadius: 999, padding: "10px 18px", fontSize: 12, fontWeight: 700,
                background: tab === t ? "#B8892B" : "#F3EBDD", color: tab === t ? "#fff" : "#3E2A1A", fontFamily: "inherit" }}>
              {t}
            </button>
          ))}
        </div>
        {tab === "Tables" && <TablesTab data={overview} reload={load} />}
        {tab === "Orders" && <OrdersTab data={overview} />}
        {tab === "Menu" && <MenuTab />}
        {tab === "Guests & Rewards" && <Panel><AdminVisits /></Panel>}
        {tab === "QR Codes" && <QrTab />}
        {tab === "Settings" && <SettingsTab />}
      </div>
    </div>
  );
}
